import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Routes, Route, Link, useLocation, useSearchParams } from "react-router-dom";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import AdminVerificationsTab from "./admin/AdminVerificationsTab";
import AdminAccountsTab from "./admin/AdminAccountsTab";
import AdminAnalyticsTab from "./admin/AdminAnalyticsTab";
import AdminGuiaTab from "./admin/AdminGuiaTab";
import AdminWaitlistTab from "./admin/AdminWaitlistTab";
import AdminContactsTab from "./admin/AdminContactsTab";
import AdminInsuranceTab from "./admin/AdminInsuranceTab";
import AdminBroadcastsTab from "./admin/AdminBroadcastsTab";
import AdminEarningsTab from "./admin/AdminEarningsTab";
import AdminCmsTab from "./admin/AdminCmsTab";
import AdminDiagnosticsTab from "./admin/AdminDiagnosticsTab";
import AdminRemindersTab from "./admin/AdminRemindersTab";
import AdminVehiclesTab from "./admin/AdminVehiclesTab";
import AdminBookingsTab from "./admin/AdminBookingsTab";
import AdminAbandonedCheckoutsTab from "./admin/AdminAbandonedCheckoutsTab";
import AdminVipPremiumTab from "./admin/AdminVipPremiumTab";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Users,
  Car,
  Calendar,
  Settings,
  AlertTriangle,
  DollarSign,
  TrendingUp,
  Check,
  X,
  Loader2,
  Search,
} from "lucide-react";
import PremiumIcon from "../components/ui/PremiumIcon";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";

export default function AdminDashboardPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ["overview", "users", "verifications", "vehicles", "bookings", "abandoned", "analytics", "guia", "waitlist", "contacts", "insurance", "reminders", "broadcasts", "earnings", "cms", "diagnostics", "settings"];
  const initialTab = (() => {
    const t = searchParams.get("tab");
    return t && VALID_TABS.includes(t) ? t : "overview";
  })();
  const [activeTab, setActiveTab] = useState(initialTab);

  // Keep tab in sync with URL (?tab=cuentas) so deep-links from FAB modal land directly.
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && VALID_TABS.includes(t) && t !== activeTab) {
      setActiveTab(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleTabChange = (next) => {
    setActiveTab(next);
    const sp = new URLSearchParams(searchParams);
    if (next === "overview") sp.delete("tab"); else sp.set("tab", next);
    setSearchParams(sp, { replace: true });
  };

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [feeSettings, setFeeSettings] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const [savingCompany, setSavingCompany] = useState(false);
  const [loading, setLoading] = useState(true);

  // Filters
  const [userFilter, setUserFilter] = useState("");
  const [vehicleFilter, setVehicleFilter] = useState("");

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, vehiclesRes, bookingsRes, disputesRes, feesRes, companyRes] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/vehicles"),
        api.get("/admin/bookings"),
        api.get("/admin/disputes"),
        api.get("/admin/fee-settings"),
        api.get("/admin/company-settings"),
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setVehicles(vehiclesRes.data.vehicles || []);
      setBookings(bookingsRes.data.bookings || []);
      setDisputes(disputesRes.data.disputes || []);
      setFeeSettings(feesRes.data);
      setCompanySettings(companyRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleUserVerification = async (userId, status) => {
    try {
      await api.put(`/admin/users/${userId}/verification`, { status });
      toast.success(`Usuario ${status === "approved" ? "aprobado" : "rechazado"}`);
      fetchAllData();
    } catch (error) {
      toast.error("Error al actualizar usuario");
    }
  };

  const handleVehicleStatus = async (vehicleId, status) => {
    try {
      await api.put(`/admin/vehicles/${vehicleId}/status`, { status });
      toast.success(`Vehículo ${status === "approved" ? "aprobado" : "rechazado"}`);
      fetchAllData();
    } catch (error) {
      toast.error("Error al actualizar vehículo");
    }
  };

  const handleUpdateFees = async () => {
    try {
      await api.put("/admin/fee-settings", feeSettings);
      toast.success("Configuración actualizada");
    } catch (error) {
      toast.error("Error al actualizar configuración");
    }
  };

  const handleUpdateCompany = async () => {
    if (!companySettings) return;
    const wa = (companySettings.whatsapp_number || "").replace(/[^0-9]/g, "");
    if (wa.length < 10) {
      toast.error("WhatsApp inválido. Incluye código de país (ej. 5216644854055).");
      return;
    }
    setSavingCompany(true);
    try {
      const res = await api.put("/admin/company-settings", {
        whatsapp_number: wa,
        support_email: (companySettings.support_email || "").trim(),
        press_email: (companySettings.press_email || "").trim(),
        careers_email: (companySettings.careers_email || "").trim(),
      });
      setCompanySettings(res.data);
      // Bust the public cache so footer pages immediately pick up new values.
      try {
        sessionStorage.removeItem("rivones_company_contact_v1");
      } catch (_) { /* ignore */ }
      toast.success("Datos de contacto actualizados");
    } catch (e) {
      toast.error("Error al guardar contacto");
    } finally {
      setSavingCompany(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
      </div>
    );
  }

  const statCards = [
    { label: t("adminDash.totalUsers"), value: stats?.total_users, icon: Users, tone: "blue" },
    { label: t("adminDash.hosts"), value: stats?.total_hosts, icon: Users, tone: "emerald" },
    { label: t("adminDash.vehiclesStat"), value: stats?.total_vehicles, icon: Car, tone: "terracotta" },
    { label: t("adminDash.bookingsStat"), value: stats?.total_bookings, icon: Calendar, tone: "blue" },
    { label: t("adminDash.pendingVerifs"), value: stats?.pending_verifications, icon: AlertTriangle, tone: "amber" },
    { label: t("adminDash.pendingVehicles"), value: stats?.pending_vehicles, icon: Car, tone: "amber" },
    { label: t("adminDash.platformRevenue"), value: `$${stats?.total_revenue?.toLocaleString()}`, icon: DollarSign, tone: "emerald" },
    { label: t("adminDash.totalGmv"), value: `$${stats?.total_gmv?.toLocaleString()}`, icon: TrendingUp, tone: "blue" },
  ];

  return (
    <div className="min-h-screen bg-sand" data-testid="admin-dashboard-page">
      <div className="container-main py-8">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-agave mb-6">
          {t("adminDash.title")}
        </h1>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <div className="overflow-x-auto -mx-1 px-1 mb-6">
            <TabsList
              data-testid="admin-tabs-list"
              className="inline-flex w-max lg:grid lg:w-full lg:grid-cols-6 gap-1 h-auto p-1"
            >
              <TabsTrigger value="overview" className="px-3 py-1.5 whitespace-nowrap text-xs sm:text-sm">
                {t("adminDash.overview")}
              </TabsTrigger>
              <TabsTrigger value="users" data-testid="tab-accounts" className="px-3 py-1.5 whitespace-nowrap text-xs sm:text-sm">
                {t("adminDash.accounts")}
              </TabsTrigger>
              <TabsTrigger value="verifications" data-testid="tab-verifications" className="px-3 py-1.5 whitespace-nowrap text-xs sm:text-sm">
                {t("adminDash.verifications")}
              </TabsTrigger>
              <TabsTrigger value="vehicles" className="px-3 py-1.5 whitespace-nowrap text-xs sm:text-sm">
                {t("adminDash.vehicles")}
              </TabsTrigger>
              <TabsTrigger value="bookings" className="px-3 py-1.5 whitespace-nowrap text-xs sm:text-sm">
                {t("adminDash.bookings")}
              </TabsTrigger>
              <TabsTrigger value="abandoned" data-testid="tab-abandoned" className="px-3 py-1.5 whitespace-nowrap text-xs sm:text-sm">
                {t("adminDash.abandoned")}
              </TabsTrigger>
              <TabsTrigger value="vip" data-testid="tab-vip" className="px-3 py-1.5 whitespace-nowrap text-xs sm:text-sm">
                {t("adminDash.vip")}
              </TabsTrigger>
              <TabsTrigger value="analytics" data-testid="tab-analytics" className="px-3 py-1.5 whitespace-nowrap text-xs sm:text-sm">
                {t("adminDash.analytics")}
              </TabsTrigger>
              <TabsTrigger value="guia" data-testid="tab-guia" className="px-3 py-1.5 whitespace-nowrap text-xs sm:text-sm">
                {t("adminDash.guide")}
              </TabsTrigger>
              <TabsTrigger value="waitlist" data-testid="tab-waitlist" className="px-3 py-1.5 whitespace-nowrap text-xs sm:text-sm">
                {t("adminDash.waitlist")}
              </TabsTrigger>
              <TabsTrigger value="contacts" data-testid="tab-contacts" className="px-3 py-1.5 whitespace-nowrap text-xs sm:text-sm">
                {t("adminDash.contacts")}
              </TabsTrigger>
              <TabsTrigger value="insurance" data-testid="tab-insurance" className="px-3 py-1.5 whitespace-nowrap text-xs sm:text-sm">
                {t("adminDash.insurance")}
              </TabsTrigger>
              <TabsTrigger value="reminders" data-testid="tab-reminders" className="px-3 py-1.5 whitespace-nowrap text-xs sm:text-sm">
                {t("adminDash.reminders")}
              </TabsTrigger>
              <TabsTrigger value="broadcasts" data-testid="tab-broadcasts" className="px-3 py-1.5 whitespace-nowrap text-xs sm:text-sm">
                {t("adminDash.broadcasts")}
              </TabsTrigger>
              <TabsTrigger value="earnings" data-testid="tab-earnings" className="px-3 py-1.5 whitespace-nowrap text-xs sm:text-sm">
                Ingresos
              </TabsTrigger>
              <TabsTrigger value="cms" data-testid="tab-cms" className="px-3 py-1.5 whitespace-nowrap text-xs sm:text-sm">
                Contenido
              </TabsTrigger>
              <TabsTrigger value="diagnostics" data-testid="tab-diagnostics" className="px-3 py-1.5 whitespace-nowrap text-xs sm:text-sm">
                Diagnóstico
              </TabsTrigger>
              <TabsTrigger value="settings" className="px-3 py-1.5 whitespace-nowrap text-xs sm:text-sm">
                Ajustes
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {statCards.map((stat, index) => (
                <div key={index} className="card-base" data-testid={`admin-stat-card-${index}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <PremiumIcon
                      icon={stat.icon}
                      variant="gradient"
                      tone={stat.tone}
                      size="md"
                    />
                  </div>
                  <p className="text-2xl font-heading font-bold">{stat.value}</p>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Users / Accounts Tab — comprehensive management */}
          <TabsContent value="users">
            <AdminAccountsTab />
          </TabsContent>

          {/* Verifications Tab */}
          <TabsContent value="verifications">
            <AdminVerificationsTab />
          </TabsContent>

          {/* Vehicles Tab */}
          <TabsContent value="vehicles">
            <AdminVehiclesTab />
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <AdminBookingsTab />
          </TabsContent>

          <TabsContent value="abandoned">
            <AdminAbandonedCheckoutsTab />
          </TabsContent>

          <TabsContent value="vip">
            <AdminVipPremiumTab />
          </TabsContent>

          {/* Visitor Analytics Tab */}
          <TabsContent value="analytics">
            <AdminAnalyticsTab />
          </TabsContent>

          <TabsContent value="guia">
            <AdminGuiaTab />
          </TabsContent>

          <TabsContent value="waitlist">
            <AdminWaitlistTab />
          </TabsContent>

          <TabsContent value="contacts">
            <AdminContactsTab />
          </TabsContent>

          <TabsContent value="insurance">
            <AdminInsuranceTab />
          </TabsContent>

          <TabsContent value="reminders">
            <AdminRemindersTab />
          </TabsContent>

          <TabsContent value="broadcasts">
            <AdminBroadcastsTab />
          </TabsContent>

          <TabsContent value="earnings">
            <AdminEarningsTab />
          </TabsContent>

          <TabsContent value="cms">
            <AdminCmsTab />
          </TabsContent>

          <TabsContent value="diagnostics">
            <AdminDiagnosticsTab />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="space-y-6 max-w-2xl">
              {/* Company contact (WhatsApp / emails) */}
              <div className="card-base" data-testid="admin-company-settings-card">
                <h3 className="font-heading font-semibold text-lg mb-1">Contacto de la empresa</h3>
                <p className="text-xs text-neutral-500 mb-6">
                  Estos datos se usan automáticamente en las páginas públicas (Contacto, FAQ, Carreras, Prensa).
                </p>

                {companySettings && (
                  <div className="space-y-5">
                    <div>
                      <label className="text-sm font-medium">WhatsApp (formato wa.me, sin "+")</label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        placeholder="5216644854055"
                        value={companySettings.whatsapp_number || ""}
                        onChange={(e) =>
                          setCompanySettings({ ...companySettings, whatsapp_number: e.target.value })
                        }
                        className="mt-2 max-w-sm"
                        data-testid="admin-company-whatsapp-input"
                      />
                      <p className="text-xs text-neutral-500 mt-1">
                        Solo dígitos. Incluye código de país (México = 521 + 10 dígitos).
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Email soporte</label>
                        <Input
                          type="email"
                          placeholder="info@rivones.com"
                          value={companySettings.support_email || ""}
                          onChange={(e) =>
                            setCompanySettings({ ...companySettings, support_email: e.target.value })
                          }
                          className="mt-2"
                          data-testid="admin-company-support-email-input"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Email prensa</label>
                        <Input
                          type="email"
                          placeholder="prensa@rivones.com"
                          value={companySettings.press_email || ""}
                          onChange={(e) =>
                            setCompanySettings({ ...companySettings, press_email: e.target.value })
                          }
                          className="mt-2"
                          data-testid="admin-company-press-email-input"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Email carreras</label>
                        <Input
                          type="email"
                          placeholder="carreras@rivones.com"
                          value={companySettings.careers_email || ""}
                          onChange={(e) =>
                            setCompanySettings({ ...companySettings, careers_email: e.target.value })
                          }
                          className="mt-2"
                          data-testid="admin-company-careers-email-input"
                        />
                      </div>
                    </div>

                    <Button
                      onClick={handleUpdateCompany}
                      className="btn-primary"
                      disabled={savingCompany}
                      data-testid="admin-save-company-btn"
                    >
                      {savingCompany ? "Guardando..." : "Guardar contacto"}
                    </Button>
                  </div>
                )}
              </div>

              {/* Fee config */}
              <div className="card-base">
                <h3 className="font-heading font-semibold text-lg mb-6">Configuración de Tarifas</h3>

                {feeSettings && (
                  <div className="space-y-6">
                  <div>
                    <label className="text-sm font-medium">Tarifa de Plataforma (%)</label>
                    <Input
                      type="number"
                      value={feeSettings.platform_fee_percent}
                      onChange={(e) =>
                        setFeeSettings({ ...feeSettings, platform_fee_percent: parseFloat(e.target.value) })
                      }
                      className="mt-2 max-w-xs"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium">IVA (%)</label>
                    <Input
                      type="number"
                      value={feeSettings.tax_percent}
                      onChange={(e) =>
                        setFeeSettings({ ...feeSettings, tax_percent: parseFloat(e.target.value) })
                      }
                      className="mt-2 max-w-xs"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium">Horas para reembolso total</label>
                    <Input
                      type="number"
                      value={feeSettings.cancellation_full_refund_hours}
                      onChange={(e) =>
                        setFeeSettings({
                          ...feeSettings,
                          cancellation_full_refund_hours: parseInt(e.target.value),
                        })
                      }
                      className="mt-2 max-w-xs"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium">Tarifa de cancelación (%)</label>
                    <Input
                      type="number"
                      value={feeSettings.cancellation_fee_percent}
                      onChange={(e) =>
                        setFeeSettings({
                          ...feeSettings,
                          cancellation_fee_percent: parseFloat(e.target.value),
                        })
                      }
                      className="mt-2 max-w-xs"
                    />
                  </div>

                  <Button onClick={handleUpdateFees} className="btn-primary" data-testid="save-fees-btn">
                    Guardar Configuración
                  </Button>
                </div>
              )}
            </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
